import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { TermsAcceptanceCheckbox } from "../components/TermsAcceptanceCheckbox";
import { LOGO_GREEN } from "../lib/brandColors";

export function InviteAcceptPage() {
    const { token: rawToken } = useParams();
    const [searchParams] = useSearchParams();
    const inviteRef = searchParams.get("ref");
    const token = decodeURIComponent((rawToken || "").trim());
    const navigate = useNavigate();
    const { setTokenFromInvite, user: existingUser, applyInviteSession, refreshMe } = useAuth();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token && !inviteRef) {
            setErr("Invitation link is incomplete.");
            setLoading(false);
            return undefined;
        }
        let m = true;
        (async () => {
            try {
                const params = inviteRef ? { ref: inviteRef } : undefined;
                const pathToken = token ? encodeURIComponent(token) : "_";
                const { data } = await api.get(`/invitations/${pathToken}`, { params });
                if (m) setInvite(data.invitation);
            } catch (e) {
                if (m) setErr(formatApiError(e.response?.data?.detail) || e.message);
            } finally { if (m) setLoading(false); }
        })();
        return () => { m = false; };
    }, [token, inviteRef]);

    const dashboardState = (data) => ({
        welcomedPoolId: data?.pool_id || invite?.pool_id,
        welcomedPoolName: data?.pool_name || invite?.pool_name,
    });

    const acceptViaInbox = async () => {
        try {
            setBusy(true);
            if (!invite?.id) {
                setErr("This invitation is no longer available. Ask the sender to resend it.");
                return;
            }
            const { data } = await api.post(`/invitations/inbox/${invite.id}/accept`);
            if (!applyInviteSession(data)) {
                await refreshMe({ clearOnFailure: false });
            }
            navigate("/dashboard", { state: dashboardState(data) });
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const acceptNewUser = async (e) => {
        e?.preventDefault();
        if (!acceptedTerms) {
            setErr("You must accept the Terms of Service to create an account.");
            return;
        }
        try {
            setBusy(true);
            const { data } = await api.post(`/invitations/${encodeURIComponent(token)}/accept`, {
                name: name || undefined,
                password: password || undefined,
                accepted_terms: acceptedTerms,
            }, {
                params: inviteRef ? { ref: inviteRef } : undefined,
            });
            await setTokenFromInvite(data);
            navigate("/dashboard", {
                state: {
                    welcomedPoolId: data.pool_id || invite?.pool_id,
                    welcomedPoolName: data.pool_name || invite?.pool_name,
                },
            });
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const requiresExistingAccount = invite?.requires_existing_account;
    const emailMatches = existingUser && existingUser.email?.toLowerCase() === invite?.email?.toLowerCase();
    const wrongAccount = existingUser && invite && !emailMatches;
    const showError = !loading && err && !invite;
    const showForm = !loading && invite;

    return (
        <div className="relative min-h-screen bg-[#1E1E1E] text-white flex items-center justify-center p-6">
            <div className={loading ? "pointer-events-none invisible" : ""} aria-hidden={loading}>
                {showError && (
                    <div className="surface p-8 max-w-md text-center">
                        <p className="text-rose-400 mb-2 text-sm">{err}</p>
                        <Link to="/login" className="text-[#00BF0D] text-sm">Back to sign in</Link>
                    </div>
                )}
                {showForm && (
                    <div className="surface p-8 max-w-md w-full" data-testid="invite-accept-card">
                        <div className="flex items-center gap-2 mb-1">
                            <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden>
                                <circle cx="16" cy="16" r="13" stroke={LOGO_GREEN} strokeWidth="2" fill="none" />
                                <path d="M16 3 A13 13 0 0 1 27.25 22.5 L16 16 Z" fill={LOGO_GREEN} opacity="0.95" />
                            </svg>
                            <span className="font-heading text-lg"><span className="text-white">Fair</span><span style={{ color: LOGO_GREEN }}>Share</span></span>
                        </div>
                        <h1 className="font-heading text-2xl tracking-tight mt-4">{invite.headline || "You're invited"}</h1>
                        <p className="text-white/55 text-sm mt-1.5">
                            {invite.subline || (
                                <>
                                    <span className="text-white">{invite.invited_by_name || invite.invited_by_email}</span> invited you to join <span className="text-white">{invite.org_name}</span>
                                    {invite.pool_name ? <> in <span className="text-white">{invite.pool_name}</span>.</> : "."}
                                </>
                            )}
                        </p>

                        {wrongAccount && (
                            <div className="mt-5 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3 text-sm text-amber-200/90">
                                You&apos;re signed in as <span className="font-medium">{existingUser.email}</span>, but this invite is for <span className="font-medium">{invite.email}</span>.
                                Sign in with the invited email to accept from your Invite Center.
                            </div>
                        )}

                        {requiresExistingAccount && !existingUser && (
                            <div className="mt-5 space-y-3">
                                <p className="text-sm text-white/60">
                                    You already have a FairShare account for <span className="text-white font-medium">{invite.email}</span>.
                                    Sign in to accept from your Invite Center.
                                </p>
                                <Link
                                    to={`/login?email=${encodeURIComponent(invite.email)}&next=${encodeURIComponent(`/invites`)}`}
                                    className="btn-neon block w-full rounded-md py-2.5 text-sm font-semibold text-center"
                                >
                                    Sign in to accept
                                </Link>
                                <Link to="/invites" className="btn-ghost block w-full rounded-md py-2.5 text-sm text-center">
                                    Open Invite Center
                                </Link>
                            </div>
                        )}

                        {emailMatches && (
                            <div className="mt-6 space-y-3">
                                <p className="text-sm text-white/55">Accept securely from your account — no password needed.</p>
                                {err && <p className="text-sm text-rose-400">{err}</p>}
                                <button type="button" onClick={acceptViaInbox} disabled={busy} className="btn-neon w-full rounded-md py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="invite-accept-submit">
                                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Accept invitation
                                </button>
                                <Link to="/invites" className="block text-center text-xs text-[#00BF0D] hover:underline">Open Invite Center</Link>
                            </div>
                        )}

                        {!requiresExistingAccount && !existingUser && (
                            <form onSubmit={acceptNewUser} className="mt-6 space-y-3" data-testid="invite-accept-form">
                                <div>
                                    <label className="label-eyebrow">Email</label>
                                    <input value={invite.email} disabled className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm opacity-70" data-testid="invite-email-disabled" />
                                </div>
                                <div>
                                    <label className="label-eyebrow">Your name</label>
                                    <input value={name} onChange={(e) => setName(e.target.value)} required className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm" data-testid="invite-name-input" />
                                </div>
                                <div>
                                    <label className="label-eyebrow">Create password</label>
                                    <div className="relative mt-1.5">
                                        <input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="input-dark w-full rounded-md px-3 py-2 pr-10 text-sm" data-testid="invite-password-input" />
                                        <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute inset-y-0 right-2.5 my-auto text-white/40">{showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                                    </div>
                                    <p className="text-[11px] text-white/30 mt-1">6+ characters</p>
                                </div>
                                <TermsAcceptanceCheckbox
                                    checked={acceptedTerms}
                                    onChange={setAcceptedTerms}
                                    id="invite-accept-terms"
                                    testId="invite-terms-checkbox"
                                />
                                {err && <p className="text-sm text-rose-400">{err}</p>}
                                <button type="submit" disabled={busy || !acceptedTerms} className="btn-neon w-full rounded-md py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="invite-accept-submit">
                                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Accept invitation
                                </button>
                            </form>
                        )}

                        <div className="mt-5 flex items-center gap-2 text-[11px] text-white/40">
                            <ShieldCheck className="h-3.5 w-3.5 text-[#00BF0D]" />
                            Bank-grade encryption · Audit-ready ledger
                        </div>
                    </div>
                )}
            </div>
            {loading && (
                <div className="absolute inset-0 grid place-items-center">
                    <Loader2 className="h-5 w-5 animate-spin text-white/40" />
                </div>
            )}
        </div>
    );
}
